import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Building2, ArrowDownToLine } from "lucide-react";

// Import modular sub-components
import {
  EarningsSummaryCards,
  EarningsBreakdown,
  MonthlyEarnings,
  RecentTransactions,
  WithdrawDialog,
  BankDetailsDialog
} from "./earnings";

export default function EarningsWallet() {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [processing, setProcessing] = useState(false);
  const [bankForm, setBankForm] = useState({
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
    bank_name: "",
    upi_id: ""
  });

  const fetchEarnings = useCallback(async () => {
    try {
      const response = await api.get("/partner/earnings");
      setEarnings(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("Failed to fetch earnings:", error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEarnings();
  }, [fetchEarnings]);

  const handleSaveBankDetails = async () => {
    if (!bankForm.account_holder_name || !bankForm.account_number || !bankForm.ifsc_code || !bankForm.bank_name) {
      toast.error("Please fill all required fields");
      return;
    }
    
    setProcessing(true);
    try {
      await api.post("/partner/bank-details", bankForm);
      toast.success("Bank details saved successfully");
      setShowBankDetails(false);
      fetchEarnings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save bank details");
    } finally {
      setProcessing(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount < 100) {
      toast.error("Minimum withdrawal amount is ₹100");
      return;
    }
    
    if (amount > (earnings?.summary?.available_balance || 0)) {
      toast.error("Insufficient balance");
      return;
    }

    setProcessing(true);
    try {
      const response = await api.post("/partner/withdrawal/request", { amount, withdrawal_method: "bank" });
      toast.success(response.data.message);
      setShowWithdraw(false);
      setWithdrawAmount("");
      fetchEarnings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request withdrawal");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const summary = earnings?.summary || {};
  const monthlyData = earnings?.monthly_breakdown || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight mb-2">Earnings</h1>
          <p className="text-muted-foreground">Track your earnings and request withdrawals</p>
        </div>
        <div className="flex gap-2">
          {!earnings?.has_bank_details && (
            <Button 
              variant="outline" 
              className="rounded-none"
              onClick={() => setShowBankDetails(true)}
              data-testid="add-bank-btn"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Add Bank Details
            </Button>
          )}
          <Button 
            className="rounded-none bg-emerald-600 hover:bg-emerald-700"
            onClick={() => setShowWithdraw(true)}
            disabled={!earnings?.has_bank_details || summary.available_balance < 100}
            data-testid="withdraw-btn"
          >
            <ArrowDownToLine className="w-4 h-4 mr-2" />
            Withdraw
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <EarningsSummaryCards 
        summary={summary} 
        hasBankDetails={earnings?.has_bank_details} 
      />

      {/* Breakdown Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <EarningsBreakdown 
          summary={summary} 
          platformCommission={earnings?.stats?.platform_commission} 
        />
        <MonthlyEarnings monthlyData={monthlyData} />
      </div>

      {/* Transactions */}
      <RecentTransactions transactions={earnings?.recent_transactions} />

      {/* Dialogs */}
      <WithdrawDialog
        open={showWithdraw}
        onOpenChange={setShowWithdraw}
        availableBalance={summary.available_balance}
        withdrawAmount={withdrawAmount}
        setWithdrawAmount={setWithdrawAmount}
        onWithdraw={handleWithdraw}
        processing={processing}
      />

      <BankDetailsDialog
        open={showBankDetails}
        onOpenChange={setShowBankDetails}
        bankForm={bankForm}
        setBankForm={setBankForm}
        onSave={handleSaveBankDetails}
        processing={processing}
      />
    </div>
  );
}
